import type { ReactNode } from "react";
import Box from "@mui/material/Box";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import SpaceDashboardIcon from "@mui/icons-material/SpaceDashboard";
import ReceiptIcon from "@mui/icons-material/Receipt";
import WebhookIcon from "@mui/icons-material/Webhook";
import WorkHistoryOutlinedIcon from "@mui/icons-material/WorkHistoryOutlined";
import EventRepeatIcon from "@mui/icons-material/EventRepeat";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import { NavLink } from "react-router";
import { InventorySnapshotIcon } from "@/components/icons/InventorySnapshotIcon";
import { OrderSnapshotIcon } from "@/components/icons/OrderSnapshotIcon";
import { SideMenuColorScheme } from "./SideMenuColorScheme";
import { DASHBOARD_PATH } from "@/features/dashboard/dashboard.paths";
import { ORDER_LIST_PATH, ORDER_SNAPSHOT_PATH } from "@/features/order/order.paths";
import { INVENTORY_LIST_PATH, INVENTORY_SNAPSHOT_PATH } from "@/features/inventory/inventory.paths";
import { JOB_LIST_PATH, JOB_SCHEDULE_PATH } from "@/features/job/job.paths";

const SIDE_MENU_WIDTH = 175;

interface SideMenuItem {
  label: string;
  to: string;
  icon: ReactNode;
}

interface SideMenuGroup {
  title: string | null;
  items: SideMenuItem[];
}

const GROUPS: SideMenuGroup[] = [
  {
    title: null,
    items: [
      { label: "Dashboard", to: DASHBOARD_PATH, icon: <SpaceDashboardIcon /> },
    ],
  },
  {
    title: "Orders",
    items: [
      { label: "List", to: ORDER_LIST_PATH, icon: <ReceiptIcon /> },
      { label: "Snapshots", to: ORDER_SNAPSHOT_PATH, icon: <OrderSnapshotIcon /> },
    ],
  },
  {
    title: "Inventory",
    items: [
      { label: "List", to: INVENTORY_LIST_PATH, icon: <Inventory2OutlinedIcon /> },
      { label: "Snapshots", to: INVENTORY_SNAPSHOT_PATH, icon: <InventorySnapshotIcon /> },
    ],
  },
  {
    title: "System",
    items: [
      { label: "Jobs", to: JOB_LIST_PATH, icon: <WorkHistoryOutlinedIcon /> },
      { label: "Schedules", to: JOB_SCHEDULE_PATH, icon: <EventRepeatIcon /> },
      { label: "Webhooks", to: "/webhook-log", icon: <WebhookIcon /> },
    ],
  },
];

/** The app's side navigation: grouped page links, with the colour scheme switch at the bottom. */
export const SideMenu = () => {
  return (
    <Box
      component="nav"
      sx={{
        bgcolor: "background.sidebar",
        display: "flex",
        flexDirection: "column",
        flexShrink: 0,
        height: "100%",
        overflowY: "auto",
        position: "relative",
        px: 1,
        pt: 1.75,
        width: SIDE_MENU_WIDTH,
        zIndex: 3,
      }}
    >
      <Stack
        direction="row"
        spacing={1}
        sx={{
          alignItems: "center",
          px: 1.5,
        }}
      >
        <Typography
          component="div"
          sx={{
            fontSize: 20,
            fontWeight: 700,
          }}
        >
          subemo
        </Typography>
      </Stack>

      {GROUPS.map((group) => (
        <Box
          key={group.items[0].to}
          sx={{ mt: group.title ? 2 : 1 }}
        >
          {group.title && (
            <Typography
              color="text.secondary"
              component="div"
              sx={{
                fontSize: 11,
                fontWeight: 500,
                letterSpacing: "0.06em",
                mb: 0.5,
                px: 1.5,
                textTransform: "uppercase",
              }}
            >
              {group.title}
            </Typography>
          )}
          <List
            disablePadding
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 0.5,
            }}
          >
            {group.items.map((item) => (
              <ListItemButton
                component={NavLink}
                key={item.to}
                to={item.to}
                sx={{
                  borderRadius: 1,
                  color: "text.secondary",
                  minHeight: 38,
                  px: 1.5,
                  "&:hover": {
                    bgcolor: "action.hover",
                  },
                  "&.active": {
                    bgcolor: "action.selected",
                    color: "text.primary",
                    "& .MuiListItemIcon-root": { color: "primary.main" },
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    color: "inherit",
                    minWidth: 0,
                    mr: 1.5,
                    "& .MuiSvgIcon-root": {
                      fontSize: 20,
                    },
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.label}
                  slotProps={{
                    primary: {
                      sx: {
                        fontWeight: 500,
                      },
                    },
                  }}
                />
              </ListItemButton>
            ))}
          </List>
        </Box>
      ))}

      <Box
        sx={{
          mt: "auto",
          pb: 1,
          pt: 3,
          px: 1
        }}
      >
        <SideMenuColorScheme />
      </Box>
    </Box>
  );
};
